import type { KitchenReleaseReason } from "./billKitchenHold";
import { api } from "./client";

// ── Types (1:1 с app/features/bills/operations_schemas.py) ────────────────

export interface OpUserBrief {
  id: number;
  names?: string | null;
  code?: string | null;
}

export interface OpAddition {
  addition_id: number;
  addition_item_id: number;
  item_names: Record<string, string>;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
}

export interface OpItem {
  bill_element_item_id: number;
  item_id: number;
  item_names: Record<string, string>;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  is_cancelled: boolean;
  /** Суровите `modifier_id` ключове; за показване са двете полета отдолу. */
  modifiers: string[];
  /** По едно {език: име} на модификатор — BE ги разделя от коментарите. */
  modifier_names: Record<string, string>[];
  /** Свободният текст към продукта (коментар от „Промени"). */
  comments: string[];
  additions: OpAddition[];
}

/** „Задържане на поръчка" (`api/billKitchenHold.ts`) на `add_items` на
 *  задържаната поръчка. */
export interface OpKitchenHold {
  /** null — ръчно, докато някой не натисне „Освободи". */
  minutes: number | null;
  /** null — още задържана. */
  released_at: string | null;
  release_reason: KitchenReleaseReason | null;
  /** Натисналият „Освободи"; null при таймер/закриване/каса. */
  released_by: OpUserBrief | null;
}

export interface OpOtherBillRef {
  id: number;
  name: string;
  waiter?: OpUserBrief | null;
}

export type OpEventType =
  | "open"
  | "add_items"
  | "split_in"
  | "split_out"
  | "cancel"
  | "discount_apply"
  | "discount_remove"
  // Печат на предварителна сметка — BE го взима от одита; без сума.
  | "preliminary_bill"
  | "payment"
  | "close"
  // Възстановяване — сметката е отворена наново СЪС СЪЩОТО id; старото
  // плащане/затваряне стоят преди него в хронологията като отделни редове.
  | "restore";

export interface OpEvent {
  type: OpEventType;
  at: string;
  user: OpUserBrief | null;
  change: string;
  balance_after: string;

  // open
  sector?: OpUserBrief | null;

  // add_items / split_in / split_out / cancel
  label?: string | null;
  /** Коментарът на поръчката; `label` пада към „Първа/Допълнителна поръчка". */
  comment?: string | null;
  items?: OpItem[];
  other_bill?: OpOtherBillRef | null;
  is_full_transfer?: boolean | null;

  // add_items — без задържане ключа го НЯМА
  kitchen_hold?: OpKitchenHold;

  // cancel
  reason?: string | null;
  is_defective?: boolean | null;

  // discount_apply / discount_remove
  discount_percent?: string | null;
  discount_type?: string | null;
  // Сумата в € — само при отстъпка на цялата сметка (`discount_type =
  // "bill_amount"`, процентът там е 0).
  discount_amount?: string | null;
  client_name?: string | null;
  is_item_level?: boolean | null;

  // payment
  payment_method_name?: string | null;
  mixed_cash?: string | null;
  mixed_card?: string | null;
  // Бакшишът на ТОВА приключване (един метод за цялата сума). Без бакшиш
  // двата ключа ги НЯМА в отговора (не са null) — събитието е байт за байт
  // старото. При отменено (възстановено) приключване — от снимката му в
  // `bill_restores`. `balance_after` остава поръчаното.
  tip_amount?: string | null;
  tip_payment_method_name?: string | null;

  // close
  duration_minutes?: number | null;

  // restore (причината е в `reason`); номерата са на отчетите, от които
  // сметката е ИЗКЛЮЧЕНА при възстановяването (null = не е била в отчет)
  previous_receipt_number?: string | null;
  previous_report_number?: number | null;
  previous_establishment_report_number?: number | null;
}

export interface OpBillBrief {
  id: number;
  name: string;
  status: "open" | "closed";
  created_at: string;
  closed_at: string | null;
  duration_minutes: number | null;
  waiter: OpUserBrief | null;
  // Колко пъти сметката е възстановявана (0 = никога) и кога за последно.
  restores_count: number;
  restored_at: string | null;
}

export interface OpSummary {
  gross_revenue: string;
  cancelled_amount: string;
  cancelled_count: number;
  discount_amount: string;
  transferred_amount: string;
  net_revenue: string;
}

export interface BillOperations {
  bill: OpBillBrief;
  summary: OpSummary;
  events: OpEvent[];
}

export const getBillOperations = async (
  billId: number,
): Promise<BillOperations> => {
  const res = await api.get<BillOperations>(`/bills/${billId}/operations`);
  return res.data;
};
